import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, NgZone, OnDestroy } from '@angular/core';
import { MatSnackBar, MatSnackBarModule, MatSnackBarRef } from '@angular/material/snack-bar';
import { RUNTIME_ENVIRONMENT } from 'environments-lib';
import { IRuntimeEnvironment } from 'interfaces-lib';
import { ErrorSeverity, IMessage, MessageLevel } from 'message-notification-service-lib';
import { AlertBannerSnackBarComponent, IAlertBannerMessage } from './alert-banner-snack-bar.component';

interface IDismissal {
  timerId: ReturnType<typeof setTimeout> | undefined;
  remainingMs: number;
  resumedAt: number;
}

@Component({
  selector: 'app-alert-banner',
  templateUrl: './alert-banner.component.html',
  styleUrls: ['./alert-banner.scss'],
  imports: [MatSnackBarModule],
})
export class AlertBannerComponent implements OnDestroy {
  private static readonly INFO_DISMISS_MS = 8000;
  private static readonly ERROR_DISMISS_MS = 30000;

  renderMode: string | undefined = undefined;
  messages: IAlertBannerMessage[] = [];

  private snackBarRef: MatSnackBarRef<AlertBannerSnackBarComponent> | null = null;
  private readonly updateUi: (update: () => void) => void;
  private readonly dismissals = new Map<IMessage, IDismissal>();

  constructor(
    private ngZone: NgZone,
    private snackBar: MatSnackBar,
    @Inject(RUNTIME_ENVIRONMENT) protected runtimeEnvironment: IRuntimeEnvironment | undefined,
  ) {
    this.updateUi = runtimeEnvironment?.runningAsChromeExtension ? this.updateUiInZone : this.updateUiNoZone;
    this.renderMode = runtimeEnvironment?.renderMode ?? 'web-app';
  }

  ngOnDestroy(): void {
    this.dismissals.forEach((dismissal) => {
      if (dismissal.timerId !== undefined) {
        clearTimeout(dismissal.timerId);
      }
    });

    this.dismissals.clear();
  }

  show(message: IMessage): void {
    const dismissMs = AlertBannerComponent.resolveDismissMs(message);

    this.messages.push({
      id: message,
      message: message.message,
      details: AlertBannerComponent.parseDetails(message.details),
      reference: message.reference?.substring(0, 8),
      theme: message.level.toString().toLowerCase(),
      time: AlertBannerComponent.getDateTime(),
      dismissMs,
    });

    this.updateUi(() => this.openOrRefreshSnackBar());
    this.scheduleDismissal(message, dismissMs);

    if (this.renderMode === 'chrome-extension') {
      this.scrollToTop();
    }
  }

  hide(message: IMessage): void {
    const index = this.messages.findIndex((m) => m.id === message);
    if (index === -1) {
      return;
    }

    this.clearDismissal(message);
    this.messages.splice(index, 1);

    if (this.messages.length === 0) {
      this.updateUi(() => {
        this.snackBarRef?.dismiss();
        this.snackBarRef = null;
      });
    }
  }

  pauseDismissal(message: IMessage): void {
    const dismissal = this.dismissals.get(message);
    if (dismissal === undefined || dismissal.timerId === undefined) {
      return;
    }

    clearTimeout(dismissal.timerId);
    dismissal.timerId = undefined;
    dismissal.remainingMs = Math.max(0, dismissal.remainingMs - (Date.now() - dismissal.resumedAt));
  }

  resumeDismissal(message: IMessage): void {
    const dismissal = this.dismissals.get(message);
    if (dismissal === undefined || dismissal.timerId !== undefined) {
      return;
    }

    this.startDismissalTimer(message, dismissal);
  }

  static resolveDismissMs(message: IMessage): number | null {
    if (message.level !== MessageLevel.ERROR) {
      return AlertBannerComponent.INFO_DISMISS_MS;
    }

    if (message.severity === ErrorSeverity.CRITICAL) {
      return null;
    }

    return AlertBannerComponent.ERROR_DISMISS_MS;
  }

  private scheduleDismissal(message: IMessage, dismissMs: number | null): void {
    if (dismissMs === null) {
      return;
    }

    const dismissal: IDismissal = { timerId: undefined, remainingMs: dismissMs, resumedAt: 0 };
    this.dismissals.set(message, dismissal);
    this.startDismissalTimer(message, dismissal);
  }

  private startDismissalTimer(message: IMessage, dismissal: IDismissal): void {
    dismissal.resumedAt = Date.now();
    dismissal.timerId = setTimeout(() => this.hide(message), dismissal.remainingMs);
  }

  private clearDismissal(message: IMessage): void {
    const dismissal = this.dismissals.get(message);
    if (dismissal === undefined) {
      return;
    }

    if (dismissal.timerId !== undefined) {
      clearTimeout(dismissal.timerId);
    }

    this.dismissals.delete(message);
  }

  private openOrRefreshSnackBar(): void {
    if (this.snackBarRef) {
      return;
    }

    const panelClass = ['kyloe-alert-banner'];
    if (this.renderMode === 'chrome-extension') {
      panelClass.push('chrome-extension');
    }

    this.snackBarRef = this.snackBar.openFromComponent(AlertBannerSnackBarComponent, {
      duration: 0,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass,
      data: {
        messages: this.messages,
        hide: (id: IMessage) => this.hide(id),
        pauseDismissal: (id: IMessage) => this.pauseDismissal(id),
        resumeDismissal: (id: IMessage) => this.resumeDismissal(id),
      },
    });

    this.snackBarRef.afterDismissed().subscribe(() => {
      this.snackBarRef = null;
    });
  }

  private scrollToTop(): void {
    document.getElementById('components')?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  static parseDetails(details?: any): string | undefined {
    if (!details) return undefined;
    if (typeof details === 'string') return details;
    if (details instanceof Array) {
      return details.every((item) => typeof item === 'string') ? details.join('<br>') : undefined;
    }
    if (details instanceof HttpErrorResponse) {
      if (details.message?.length > 0) {
        let msg = details.message;
        if (details.error?.message) msg += ` (${details.error.message})`;
        if (details.error?.Message) msg += ` (${details.error.Message})`;
        return msg;
      }
      return `${details.status}: ${details.statusText}`;
    }
    if (details instanceof Error) return details.message;
    return undefined;
  }

  static getDateTime(): string {
    const d = new Date();
    const date = ('' + d.getUTCDate()).padStart(2, '0');
    const month = ('' + (d.getUTCMonth() + 1)).padStart(2, '0');
    const year = '' + d.getUTCFullYear();
    const hour = ('' + d.getUTCHours()).padStart(2, '0');
    const minute = ('' + d.getUTCMinutes()).padStart(2, '0');
    const second = ('' + d.getUTCSeconds()).padStart(2, '0');
    return `${year}-${month}-${date} ${hour}:${minute}:${second} UTC`;
  }

  private updateUiNoZone(update: () => void): void {
    update();
  }

  private updateUiInZone(update: () => void): void {
    this.ngZone.run(() => update());
  }
}
